import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Sparkles, Car, CalendarCheck, LayoutDashboard, Wallet, RotateCcw, Loader2 } from "lucide-react";
import { useAuth, api } from "../App";

export const ReviewerDemoBanner = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("gp_demo_banner_dismissed") === "1");
  const [resetting, setResetting] = useState(false);

  if (!user?.is_demo_reviewer || dismissed) return null;

  const handleReset = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      await api.post("/demo/reset");
      toast.success(t("demoBanner.resetSuccess"));
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(t("demoBanner.resetError"));
      setResetting(false);
    }
  };

  const isHost = user.role === "host";
  const links = isHost
    ? [
        { to: "/host/dashboard", icon: LayoutDashboard, label: t("demoBanner.hostDashboard"), tid: "demo-banner-link-dashboard" },
        { to: "/host/listings", icon: Car, label: t("demoBanner.hostListings"), tid: "demo-banner-link-listings" },
        { to: "/host/analytics", icon: Wallet, label: t("demoBanner.hostEarnings"), tid: "demo-banner-link-earnings" },
      ]
    : [
        { to: "/cars/veh_gp_demo_versa", icon: Car, label: t("demoBanner.renterBook"), tid: "demo-banner-link-book" },
        { to: "/trips", icon: CalendarCheck, label: t("demoBanner.renterTrips"), tid: "demo-banner-link-trips" },
      ];

  return (
    <div data-testid="reviewer-demo-banner" className="bg-emerald-700 text-white px-4 py-2.5">
      <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
        <span className="flex items-center gap-1.5 font-semibold shrink-0">
          <Sparkles className="w-4 h-4" />
          {t("demoBanner.title")}
        </span>
        <span className="hidden sm:inline text-emerald-100">
          {isHost ? t("demoBanner.subtitleHost") : t("demoBanner.subtitleRenter")}
        </span>
        <span className="flex flex-wrap items-center gap-2">
          {links.map(({ to, icon: Icon, label, tid }) => (
            <Link
              key={to}
              to={to}
              data-testid={tid}
              className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 transition-colors rounded-full px-3 py-1 font-medium"
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </Link>
          ))}
          <button
            data-testid="demo-banner-reset"
            onClick={handleReset}
            disabled={resetting}
            className="inline-flex items-center gap-1 bg-white text-emerald-800 hover:bg-emerald-50 transition-colors rounded-full px-3 py-1 font-semibold disabled:opacity-60"
          >
            {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            {t("demoBanner.reset")}
          </button>
        </span>
        <button
          data-testid="demo-banner-dismiss"
          onClick={() => {
            sessionStorage.setItem("gp_demo_banner_dismissed", "1");
            setDismissed(true);
          }}
          aria-label={t("common.close")}
          className="ml-auto p-1 rounded hover:bg-white/20 transition-colors shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ReviewerDemoBanner;
